import {
  ELLIPSOIDAL_HEIGHT_LABEL,
  ellipsoidalHeightFromCoordinates,
  formatElevationFeet,
  formatReferenceFrameValue,
  shouldShowEllipsoidalHeight,
} from '@civilgrid/utils/src/geoItems/elevationDisclosure';
import type { GeoItem } from '~/common/types';

type GeoItemAccuracy = NonNullable<GeoItem['accuracy']>;
type PositionLike = readonly number[] | null | undefined;

/** A label/value pair ready for a `ReadOnlyField` row. */
export interface AccuracyProvenanceField {
  label: string;
  value: string;
}

/**
 * The GNSS provenance rows kept behind "See more", in the order mobile's
 * `SecondaryAccuracyDetails` renders them. Optional receiver fields are omitted rather than
 * rendered empty, so a device-GPS capture collapses to the method, solution type and frame.
 *
 * Reference Frame reads differently here than on mobile by decision: this passes through
 * {@link formatReferenceFrameValue}, which maps the `CRS84` and `unknown` sentinels, where mobile
 * prints the receiver string verbatim.
 */
export function accuracyProvenanceFields(
  accuracy: GeoItemAccuracy,
  coordinates?: PositionLike,
): AccuracyProvenanceField[] {
  const fields: AccuracyProvenanceField[] = [
    { label: 'Measurement Method', value: accuracy.measurementMethod ?? 'Unknown' },
    { label: 'Solution Type', value: accuracy.solutionType },
    { label: 'Reference Frame', value: formatReferenceFrameValue(accuracy) },
  ];

  if (accuracy.receiverMake) {
    fields.push({ label: 'Receiver Make', value: accuracy.receiverMake });
  }

  if (accuracy.receiverModel) {
    fields.push({ label: 'Receiver Model', value: accuracy.receiverModel });
  }

  if (accuracy.receiverSerialNumber) {
    fields.push({ label: 'Receiver Serial', value: accuracy.receiverSerialNumber });
  }

  if (accuracy.recordedAt) {
    fields.push({ label: 'Recorded At', value: new Date(accuracy.recordedAt).toLocaleString() });
  }

  // Ellipsoidal height last, as on mobile. The primary rows carry it only as the no-elevation
  // fallback, so it belongs here whenever an elevation already occupies that slot.
  const ellipsoidalHeight = ellipsoidalHeightFromCoordinates(coordinates);

  if (ellipsoidalHeight !== null && !shouldShowEllipsoidalHeight(accuracy, coordinates)) {
    fields.push({ label: ELLIPSOIDAL_HEIGHT_LABEL, value: formatElevationFeet(ellipsoidalHeight) });
  }

  return fields;
}
